import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useValueChurnProbe } from "../utils/loopDebug";
import {
  CreateFormTemplateFromCloneRequest,
  CreateFormTemplateFromScratchRequest,
  FormCategory,
  FormTemplate,
  ListFormTemplatesResponse,
  UpdateFormTemplateRequest,
} from "../types/formTemplates";
import { API_CONFIG, getApiUrl } from "../config/api";
import { useSalon } from "./SalonContext";
import { useApp } from "./AppContext";
import { useFeatureAccess } from "./FeatureAccessContext";

interface FormTemplatesContextType {
  standardTemplates: FormTemplate[];
  customTemplates: FormTemplate[];
  isLoading: boolean;
  error: string | null;
  invalidateKey: number;
  fetchTemplates: (params?: {
    category?: FormCategory;
  }) => Promise<ListFormTemplatesResponse | null>;
  fetchTemplateById: (templateId: string) => Promise<FormTemplate | null>;
  createTemplateFromScratch: (
    req: CreateFormTemplateFromScratchRequest,
  ) => Promise<FormTemplate>;
  cloneTemplate: (
    req: CreateFormTemplateFromCloneRequest,
  ) => Promise<FormTemplate>;
  updateTemplate: (
    templateId: string,
    req: UpdateFormTemplateRequest,
  ) => Promise<FormTemplate>;
  deleteTemplate: (templateId: string) => Promise<void>;
  bumpInvalidateKey: () => void;
}

const FormTemplatesContext = createContext<FormTemplatesContextType | null>(
  null,
);

function extractBackendMessage(err: unknown): string {
  if (err instanceof Error && err.message) return err.message;
  return "An unexpected error occurred";
}

export function FormTemplatesProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [standardTemplates, setStandardTemplates] = useState<FormTemplate[]>(
    [],
  );
  const [customTemplates, setCustomTemplates] = useState<FormTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invalidateKey, setInvalidateKey] = useState(0);

  const { businessInfo } = useSalon() as any;
  const { fetchWithAuth } = useApp();
  const { access } = useFeatureAccess();
  // PHI templates are gated server-side via formsAndWaivers.canViewTemplates;
  // /access zeros it out when the salon's business_vertical doesn't qualify
  // (or the role lacks permission). Skip the auto-fetch in that case so we
  // don't generate 4xx noise.
  const canViewTemplates =
    access?.section_permissions?.formsAndWaivers?.canViewTemplates === true;

  const bumpInvalidateKey = useCallback(() => {
    setInvalidateKey((k) => k + 1);
  }, []);

  const fetchTemplates = useCallback(
    async (params?: { category?: FormCategory }) => {
      if (!businessInfo?.id) return null;

      try {
        setIsLoading(true);
        setError(null);

        const queryParts: string[] = ["source=all"];
        if (params?.category) queryParts.push(`category=${params.category}`);

        const response: ListFormTemplatesResponse = await fetchWithAuth(
          getApiUrl(API_CONFIG.ENDPOINTS.FORM_TEMPLATES, queryParts.join("&"), {
            salonId: businessInfo.id,
          }),
        );

        setStandardTemplates(response?.standard_templates ?? []);
        setCustomTemplates(response?.custom_templates ?? []);
        return response;
      } catch (err) {
        setError(extractBackendMessage(err));
        console.error("Error fetching form templates:", err);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [businessInfo?.id, fetchWithAuth],
  );

  const fetchTemplateById = useCallback(
    async (templateId: string): Promise<FormTemplate | null> => {
      if (!businessInfo?.id) return null;

      try {
        setError(null);
        const response: FormTemplate = await fetchWithAuth(
          getApiUrl(API_CONFIG.ENDPOINTS.FORM_TEMPLATE_BY_ID, "", {
            salonId: businessInfo.id,
            templateId,
          }),
        );
        return response;
      } catch (err) {
        setError(extractBackendMessage(err));
        console.error("Error fetching form template by id:", err);
        throw err;
      }
    },
    [businessInfo?.id, fetchWithAuth],
  );

  const createTemplateFromScratch = useCallback(
    async (
      req: CreateFormTemplateFromScratchRequest,
    ): Promise<FormTemplate> => {
      if (!businessInfo?.id) throw new Error("No salon ID available");

      try {
        setIsLoading(true);
        setError(null);

        const response: FormTemplate = await fetchWithAuth(
          getApiUrl(API_CONFIG.ENDPOINTS.FORM_TEMPLATES, "", {
            salonId: businessInfo.id,
          }),
          { method: "POST", body: JSON.stringify(req) },
        );

        await fetchTemplates();
        bumpInvalidateKey();
        return response;
      } catch (err) {
        setError(extractBackendMessage(err));
        console.error("Error creating form template:", err);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [businessInfo?.id, fetchWithAuth, fetchTemplates, bumpInvalidateKey],
  );

  const cloneTemplate = useCallback(
    async (req: CreateFormTemplateFromCloneRequest): Promise<FormTemplate> => {
      if (!businessInfo?.id) throw new Error("No salon ID available");

      try {
        setIsLoading(true);
        setError(null);

        const response: FormTemplate = await fetchWithAuth(
          getApiUrl(API_CONFIG.ENDPOINTS.FORM_TEMPLATES, "", {
            salonId: businessInfo.id,
          }),
          { method: "POST", body: JSON.stringify(req) },
        );

        await fetchTemplates();
        bumpInvalidateKey();
        return response;
      } catch (err) {
        setError(extractBackendMessage(err));
        console.error("Error cloning form template:", err);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [businessInfo?.id, fetchWithAuth, fetchTemplates, bumpInvalidateKey],
  );

  const updateTemplate = useCallback(
    async (
      templateId: string,
      req: UpdateFormTemplateRequest,
    ): Promise<FormTemplate> => {
      if (!businessInfo?.id) throw new Error("No salon ID available");

      try {
        setIsLoading(true);
        setError(null);

        const response: FormTemplate = await fetchWithAuth(
          getApiUrl(API_CONFIG.ENDPOINTS.FORM_TEMPLATE_BY_ID, "", {
            salonId: businessInfo.id,
            templateId,
          }),
          { method: "PUT", body: JSON.stringify(req) },
        );

        await fetchTemplates();
        bumpInvalidateKey();
        return response;
      } catch (err) {
        setError(extractBackendMessage(err));
        console.error("Error updating form template:", err);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [businessInfo?.id, fetchWithAuth, fetchTemplates, bumpInvalidateKey],
  );

  const deleteTemplate = useCallback(
    async (templateId: string): Promise<void> => {
      if (!businessInfo?.id) throw new Error("No salon ID available");

      // Optimistic removal with rollback on failure.
      const snapshot = customTemplates;
      setCustomTemplates((prev) => prev.filter((t) => t.id !== templateId));

      try {
        setError(null);
        await fetchWithAuth(
          getApiUrl(API_CONFIG.ENDPOINTS.FORM_TEMPLATE_BY_ID, "", {
            salonId: businessInfo.id,
            templateId,
          }),
          { method: "DELETE" },
        );
        bumpInvalidateKey();
      } catch (err) {
        setCustomTemplates(snapshot);
        setError(extractBackendMessage(err));
        console.error("Error deleting form template:", err);
        throw err;
      }
    },
    [businessInfo?.id, fetchWithAuth, customTemplates, bumpInvalidateKey],
  );

  useEffect(() => {
    if (businessInfo?.id && canViewTemplates) {
      fetchTemplates();
    }
  }, [businessInfo?.id, canViewTemplates, fetchTemplates]);

  // [LOOP] churn probe
  useValueChurnProbe("FormTemplatesContext", {
    standardTemplates,
    customTemplates,
    isLoading,
    error,
    invalidateKey,
  });

  // Memoized so consumers don't re-render on every provider render; every
  // method above is useCallback'd, so the deps are all stable references.
  const value: FormTemplatesContextType = useMemo(
    () => ({
      standardTemplates,
      customTemplates,
      isLoading,
      error,
      invalidateKey,
      fetchTemplates,
      fetchTemplateById,
      createTemplateFromScratch,
      cloneTemplate,
      updateTemplate,
      deleteTemplate,
      bumpInvalidateKey,
    }),
    [
      standardTemplates,
      customTemplates,
      isLoading,
      error,
      invalidateKey,
      fetchTemplates,
      fetchTemplateById,
      createTemplateFromScratch,
      cloneTemplate,
      updateTemplate,
      deleteTemplate,
      bumpInvalidateKey,
    ],
  );

  return (
    <FormTemplatesContext.Provider value={value}>
      {children}
    </FormTemplatesContext.Provider>
  );
}

export function useFormTemplates() {
  const context = useContext(FormTemplatesContext);
  if (!context) {
    throw new Error(
      "useFormTemplates must be used within a FormTemplatesProvider",
    );
  }
  return context;
}
